/**
 * DraftFeedbackView — Version Compare's "Draft Feedback" tab for the person a private draft is shared with (wireframe
 * jobs-library-ownership-v3.html cmpView; Ownership design, part 3). The approval review's side-by-side smart-diff
 * (SmartDiffViewerV3) of the latest draft against the version it grew from: clicking a changed sentence adds a
 * comment, held "not submitted yet" (seen only by its author) until Submit comments (top right), which saves them,
 * tells the owner and closes the view. Submitted comments stack per sentence: anyone shared replies to a comment or
 * adds their own; authors edit or delete their own (a delete takes its replies) until the holder resolves them. A
 * comment not submitted yet is edited in place or removed.
 *   jobId, ownerName
 *   onPendingChange(count) — the unsubmitted comments (the page asks before closing with some)
 *   onCountChange(count) — the submitted comments on the draft (the tab shows it)
 *   onDone() — submitted: close the view
 *   onRefused({ message }) — the draft changed, was sent or the share ended: the page reloads
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import { DeleteOutlined, EditOutlined, EnterOutlined } from '@ant-design/icons';
import { SbButton, SbNotification, SbSpin, SbTooltip, TooltipTriggerStyled } from 'uiCore';
import SbInput from 'uiCore/SbInput/SbInput';
import useAuthStore from 'Store/useAuthStore';
import {
  deleteDraftFeedback, editDraftFeedback, getDraftDiff, getDraftFeedback, submitDraftFeedback,
} from 'Api/apiService';
import SmartDiffViewerV3 from 'Components/SbDiffCheckerV3/SmartDiffViewerV3';
import { VIEW_MODE } from 'constants/jobs.constants';
import {
  ADD_COMMENT_LABEL, ADD_REPLY_LABEL, COMMENT_PLACEHOLDER, EDIT_PLACEHOLDER, INVALID_COMMENT_LABEL, MAX_COMMENT_LENGTH,
  REPLY_PLACEHOLDER, SUBMIT_COMMENTS_LABEL, TOOLTIP_SUBMIT_DISABLED, TOO_MANY_COMMENTS, getCommentsSentToast,
  getDeleteConfirm, getFeedbackError, getFeedbackNote, getThreads, isCommentableItem, isOwnComment, withPendingComment,
} from 'Utils/draftFeedback';
import {
  CommentBoxActionsStyled, CommentBoxStyled, CommentMetaStyled, FeedbackBarStyled, FeedbackNoteStyled, IconActionStyled,
  ThreadHeaderStyled, ThreadsStyled,
} from './DraftFeedback.styles';
import { CommentsToggle, ThreadCard, countThreads } from './CommentThreadParts';
import { SectionLoaderStyled } from 'styles/common.styles';
import { OWNERSHIP_TOAST_CONFIG } from 'Utils/jdOwnership';

let tempIds = 0;
const nextTempId = () => { tempIds += 1; return `pending-${tempIds}`; };
// A small text box with Cancel / confirm (adding a comment or a reply, editing one's own)
// The add / reply / edit box, in the comment cards' style (see CommentBoxStyled); reply: indented like a reply
const CommentInput = ({ placeholder, confirmLabel, initial = '', onCancel, onConfirm, reply = false }) => {
  const [text, setText] = useState(initial);
  return (
    <CommentBoxStyled $reply={reply} data-testid="comment-box">
      <ThreadHeaderStyled><b>You</b></ThreadHeaderStyled>
      <SbInput textarea rows={2} autoSize={{ minRows: 2, maxRows: 6 }} placeholder={placeholder} value={text}
        maxLength={MAX_COMMENT_LENGTH} onChange={(e) => setText(e.target.value)} />
      <CommentBoxActionsStyled>
        <SbButton size="small" onClick={onCancel}>Cancel</SbButton>
        <SbButton size="small" buttonType="primary" disabled={!text.trim()} onClick={() => onConfirm(text.trim())}>
          {confirmLabel}
        </SbButton>
      </CommentBoxActionsStyled>
    </CommentBoxStyled>
  );
};

CommentInput.propTypes = {
  placeholder: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  initial: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  reply: PropTypes.bool,
};

const DraftFeedbackView = ({
  jobId, ownerName = '', onPendingChange = undefined, onCountChange = undefined, onDone, onRefused = undefined,
}) => {
  const email = useAuthStore((s) => s.email);
  const [diffData, setDiffData] = useState(null);
  const [comments, setComments] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  // The sentences whose comments are open (collapsed by default behind each sentence's comment button)
  const [openAnchors, setOpenAnchors] = useState(() => new Set());
  const setAnchorOpen = (anchor, open) => setOpenAnchors((prev) => {
    const next = new Set(prev);
    if (open) next.add(anchor); else next.delete(anchor);
    return next;
  });

  const refuse = (err) => {
    const failure = getFeedbackError(err);
    SbNotification.error('Comments not saved', failure.message);
    if (failure.reload && onRefused) onRefused(failure);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([getDraftDiff(jobId), getDraftFeedback(jobId)])
      .then(([diff, list]) => {
        if (cancelled) return;
        setDiffData(diff);
        setComments(list?.comments || []);
      })
      .catch((err) => { if (!cancelled) refuse(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps -- refuse: the parent's latest is enough

  useEffect(() => { if (onPendingChange) onPendingChange(pending.length); }, [pending.length]); // eslint-disable-line react-hooks/exhaustive-deps -- once per change
  useEffect(() => { if (onCountChange) onCountChange(comments.length); }, [comments.length]); // eslint-disable-line react-hooks/exhaustive-deps -- once per change

  const addPending = (comment) => {
    const next = withPendingComment(pending, { ...comment, feedback_id: nextTempId(), author_email: email });
    if (!next) {
      SbNotification.warning(TOO_MANY_COMMENTS);
      return;
    }
    setPending(next);   // no toast: the Submit comments button turns on (with the count) instead
    setAnchorOpen(comment.anchor_text, true);   // keep that sentence's thread open so the new comment shows
  };

  const run = async (action, onRefusal = undefined) => {
    setBusy(true);
    try {
      const result = await action();
      setComments(result?.comments || []);
      return true;
    } catch (err) {
      if (onRefusal) onRefusal(err);
      refuse(err);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    const toSend = pending.map(({ anchor_text: anchor, text, reply_to: parent }) => (
      parent ? { text, reply_to: parent } : { anchor_text: anchor, text }));
    const count = toSend.length;
    // the server names the comments it refused (e.g. a sentence that is no longer a change): mark them
    const markRefused = (err) => {
      const refused = new Set(err?.response?.data?.invalid_comments || []);
      if (refused.size) setPending((prev) => prev.map((p, i) => ({ ...p, invalid: refused.has(i) })));
    };
    if (await run(() => submitDraftFeedback(jobId, toSend), markRefused)) {
      setPending([]);
      setOpenAnchors(new Set());   // after sending, everything folds again
      SbNotification.success(getCommentsSentToast(count, ownerName), '', OWNERSHIP_TOAST_CONFIG);
      onDone();
    }
  };

  const handleDelete = (comment, hasReplies) => {
    Modal.confirm({
      ...getDeleteConfirm(hasReplies),
      onOk: () => run(() => deleteDraftFeedback(comment.feedback_id)),
    });
  };

  const card = (comment, isReply, anchor) => {
    const mine = isOwnComment(comment, email);
    const editable = mine && !comment.pending;
    if (editing === comment.feedback_id) {
      return (
        <CommentInput
          key={comment.feedback_id}
          reply={isReply}
          placeholder={EDIT_PLACEHOLDER}
          confirmLabel="Save"
          initial={comment.text}
          onCancel={() => setEditing(null)}
          onConfirm={async (text) => {
            // a comment not submitted yet is only held here: edit it in place (the server never saw it)
            if (comment.pending) {
              setPending((prev) => prev.map((p) => (p.feedback_id === comment.feedback_id
                ? { ...p, text, invalid: false } : p)));
              setEditing(null);
            } else if (await run(() => editDraftFeedback(comment.feedback_id, text))) {
              setEditing(null);
            }
          }}
        />
      );
    }
    // one icon action: a small grey button that darkens on hover, with its name as tooltip and accessible name
    const action = (label, Icon, onClick, disabled = busy) => (
      <SbTooltip key={label} content={label}>
        <TooltipTriggerStyled>
          <IconActionStyled type="button" aria-label={label} disabled={disabled} onClick={onClick}><Icon /></IconActionStyled>
        </TooltipTriggerStyled>
      </SbTooltip>
    );
    return (
      <ThreadCard key={comment.feedback_id} comment={comment} isReply={isReply} testId="feedback-comment"
        actions={(
          <>
            {!comment.pending && !isReply && action('Reply', EnterOutlined,
              () => setReplyTo({ id: comment.feedback_id, anchor }))}
            {(editable || comment.pending) && action('Edit', EditOutlined, () => setEditing(comment.feedback_id))}
            {editable && action('Delete', DeleteOutlined,
              () => handleDelete(comment, !isReply && comments.some((r) => r.reply_to === comment.feedback_id)))}
            {comment.pending && action('Remove', DeleteOutlined,
              () => setPending((prev) => prev.filter((p) => p.feedback_id !== comment.feedback_id)), false)}
          </>
        )}
      >
        {comment.invalid && <CommentMetaStyled data-testid="invalid-comment">{INVALID_COMMENT_LABEL}</CommentMetaStyled>}
      </ThreadCard>
    );
  };

  const feedback = {
    isCommentable: isCommentableItem,
    popoverFor: (item, close) => (
      <CommentInput
        placeholder={COMMENT_PLACEHOLDER}
        confirmLabel={ADD_COMMENT_LABEL}
        onCancel={close}
        onConfirm={(text) => { addPending({ anchor_text: item.text, text }); close(); }}
      />
    ),
    // the comment button at the sentence's top right: the count, an orange dot when one is not sent yet
    badgeFor: (item) => {
      const threads = getThreads(comments, item.text, pending);
      if (!threads.length) return null;
      const { count, unsent } = countThreads(threads);
      const open = openAnchors.has(item.text);
      return <CommentsToggle count={count} unsent={unsent} open={open} onToggle={() => setAnchorOpen(item.text, !open)} />;
    },
    threadsFor: (item) => {
      const threads = getThreads(comments, item.text, pending);
      if (!threads.length || !(openAnchors.has(item.text) || replyTo?.anchor === item.text)) return null;
      return (
        <ThreadsStyled>
          {threads.map(({ root, replies }) => (
            <React.Fragment key={root.feedback_id}>
              {card(root, false, item.text)}
              {replies.map((r) => card(r, true, item.text))}
              {replyTo?.id === root.feedback_id && (
                <CommentInput
                  reply
                  placeholder={REPLY_PLACEHOLDER}
                  confirmLabel={ADD_REPLY_LABEL}
                  onCancel={() => setReplyTo(null)}
                  onConfirm={(text) => {
                    addPending({ anchor_text: replyTo.anchor, text, reply_to: root.feedback_id });
                    setReplyTo(null);
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </ThreadsStyled>
      );
    },
  };

  // the first load: the spinner centred both ways in the compare area (the shared section loader)
  if (loading) return <SectionLoaderStyled data-testid="feedback-loading"><SbSpin size="large" /></SectionLoaderStyled>;
  const viewData = diffData?.diff ? { ...diffData, left_pane: diffData.diff.left_pane || [],
    right_pane: diffData.diff.right_pane || [] } : null;

  return (
    <>
      <FeedbackBarStyled>
        <FeedbackNoteStyled>{getFeedbackNote(ownerName)}</FeedbackNoteStyled>
        <SbTooltip content={TOOLTIP_SUBMIT_DISABLED} disabled={pending.length > 0}>
          <TooltipTriggerStyled>
            <SbButton buttonType="primary" loading={busy} disabled={!pending.length || busy} onClick={handleSubmit}>
              {SUBMIT_COMMENTS_LABEL}{pending.length ? ` (${pending.length})` : ''}
            </SbButton>
          </TooltipTriggerStyled>
        </SbTooltip>
      </FeedbackBarStyled>
      {viewData && <SmartDiffViewerV3 diffData={viewData} viewMode={VIEW_MODE.FEEDBACK} readOnly feedback={feedback} />}
    </>
  );
};

DraftFeedbackView.propTypes = {
  jobId: PropTypes.string.isRequired,
  ownerName: PropTypes.string,
  onPendingChange: PropTypes.func,
  onCountChange: PropTypes.func,
  onDone: PropTypes.func.isRequired,
  onRefused: PropTypes.func,
};

export default DraftFeedbackView;
